import { css } from '@emotion/react';
import { FMT } from '../../../util/formatter-service';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import { CellClassParams, ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { BulkUploadSummary } from '../../../services/queries/BulkDealDocsQueries';
import { selectedBulkDealDocsActionState, selectedBulkUploadIdState } from './bulkDealDocsState';

interface IBulkDealDocsUploadsGridProps {
  data: BulkUploadSummary[];
}

export function BulkDealDocsUploadsGrid({ data }: IBulkDealDocsUploadsGridProps) {
  const theme = useTheme();
  const setSelectedAction = useSetAtom(selectedBulkDealDocsActionState);
  const setSelectedUploadId = useSetAtom(selectedBulkUploadIdState);

  const handleViewDetails = useCallback(
    (uploadId: number) => {
      setSelectedUploadId(uploadId);
      setSelectedAction('view');
    },
    [setSelectedAction, setSelectedUploadId]
  );

  const statusStyles = useMemo(
    () => ({
      uploading: css`
        color: ${theme.palette.info.main};
        font-weight: bold;
      `,
      processing: css`
        color: ${theme.palette.info.main};
        font-weight: bold;
      `,
      completed: css`
        color: ${theme.palette.success.main};
        font-weight: bold;
      `,
      error: css`
        color: ${theme.palette.error.main};
        font-weight: bold;
      `,
    }),
    [theme]
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: 'fileName',
        headerName: 'File Name',
        minWidth: 250,
        flex: 2,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        cellRenderer: (params: ICellRendererParams<BulkUploadSummary>) => {
          if (!params.value) {
            return null;
          }
          const status = params.value as keyof typeof statusStyles;
          const style = statusStyles[status];
          return <span css={style}>{params.value.toUpperCase()}</span>;
        },
      },
      {
        field: 'totalFiles',
        headerName: 'Total Files',
        width: 110,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'processedFiles',
        headerName: 'Processed',
        width: 110,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'successCount',
        headerName: 'Success',
        width: 100,
        type: 'numericColumn',
        cellStyle: {
          color: theme.palette.success.main,
          fontWeight: 'bold',
        },
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'failedCount',
        headerName: 'Failed',
        width: 100,
        type: 'numericColumn',
        cellStyle: (params: CellClassParams<BulkUploadSummary>) => ({
          color: params.value > 0 ? theme.palette.error.main : theme.palette.text.primary,
          fontWeight: params.value > 0 ? 'bold' : 'normal',
        }),
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'skippedCount',
        headerName: 'Skipped',
        width: 100,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'fileSize',
        headerName: 'File Size',
        width: 110,
      },
      {
        field: 'uploadedBy',
        headerName: 'Uploaded By',
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'createdAt',
        headerName: 'Uploaded At',
        width: 180,
        valueFormatter: (params: ValueFormatterParams<BulkUploadSummary>) => {
          if (params.value) {
            return FMT.format('dateTime', params.value);
          }
          return '';
        },
      },
      {
        headerName: 'Actions',
        width: 100,
        cellRenderer: (params: ICellRendererParams<BulkUploadSummary>) => {
          const rowData = params.data;
          if (!rowData) {
            return null;
          }
          return (
            <IconButton
              size='small'
              color='primary'
              onClick={() => handleViewDetails(rowData.id)}
              title='View Details'
            >
              <VisibilityIcon fontSize='small' />
            </IconButton>
          );
        },
        sortable: false,
        filter: false,
      },
    ],
    [handleViewDetails, statusStyles, theme]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    []
  );

  return (
    <FadeInGridWrapper>
      <Stack height='80vh' mt='1rem'>
        <AgTable
          columnDefs={columnDefs}
          rowData={data}
          defaultColDef={defaultColDef}
          rowGroupPanelShow='never'
        />
      </Stack>
    </FadeInGridWrapper>
  );
}
